import { State } from 'utils/state';
import { recordDegradedEnter, recordDegradedExit } from 'state/session/syncTelemetry';

/**
 * Local-only, reactive signal-health flag (AVS-4330). `degraded` is true while the broadcast
 * socket has given up reconnecting and the client is on the slow-poll read-repair fallback.
 *
 * Deliberately a standalone `State`, NOT a `userSession` field: a session field would trip
 * `syncToServer`'s "no relevant keys changed" branch and emit an `emitOnly` broadcast on every
 * toggle. A UI indicator can subscribe to this directly.
 */
export const syncStatus = new State<{ degraded: boolean }>({ degraded: false });

/**
 * Set the degraded flag; only writes (and emits) when the value actually changes.
 *
 * The change-guard is also what makes this the single telemetry choke point (AVS-4332): every
 * degraded entry path (the AVS-4331 grace-window timeout, a rejected connect, and
 * `WsClientGaveUp`) and the reconnect exit all funnel through here, so one recording site covers
 * them with no double counting — and the unconditional `stopSyncDegradedPoll()` on every healthy
 * connect records no spurious exit.
 */
export function setSyncDegraded(degraded: boolean): void {
  if (syncStatus.get('degraded') !== degraded) {
    if (degraded) {
      recordDegradedEnter();
    } else {
      recordDegradedExit();
    }
    syncStatus.setValues({ degraded });
  }
}

export function isSyncDegraded(): boolean {
  return syncStatus.get('degraded');
}
